import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useRef, useState } from 'react';

// reducer - state management
import { LOGIN, LOGOUT, IMPERSONATE, EXIT_IMPERSONATION, UPDATE_PROFILE } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios, { setAuthToken } from 'utils/axios';
import { getErrorMessage } from 'utils/axios';

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  isImpersonating: false,
  impersonatedBy: null,
  user: null
};

/**
 * Set session state - updates axios defaults only, NOT localStorage.
 * Access tokens are kept in memory only for security.
 */
function setSession(serviceToken) {
  setAuthToken(serviceToken);
  // Set global flag for axios interceptor to know if we're impersonating
  window.__isImpersonating = false;
}

/**
 * Set impersonation session - disables auto-refresh
 */
function setImpersonationSession(serviceToken) {
  setAuthToken(serviceToken);
  // Signal to axios interceptor: do NOT auto-refresh during impersonation
  window.__isImpersonating = true;
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export function JWTProvider({ children }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const userRef = useRef(state.user);
  const [pendingMfaChallenge, setPendingMfaChallenge] = useState(null);

  useEffect(() => {
    userRef.current = state.user;
  }, [state.user]);

  const setBillingState = useCallback((billing) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    dispatch({
      type: UPDATE_PROFILE,
      payload: {
        user: {
          ...currentUser,
          billing
        }
      }
    });
  }, []);

  // Logout handler - clears local state
  const handleLogout = useCallback(() => {
    setSession(null);
    window.__isImpersonating = false;
    setPendingMfaChallenge(null);
    dispatch({ type: LOGOUT });
  }, []);

  const refreshBillingStatus = useCallback(async () => {
    const response = await axios.get('/v1/billing/status');
    const billing = response.data?.billing || null;
    if (billing) {
      setBillingState(billing);
    }
    return billing;
  }, [setBillingState]);

  const getSessionSummary = useCallback(async () => {
    const response = await axios.get('/v1/account/sessions');
    const data = response.data || {};
    return {
      activeSessionCount: data.active_session_count ?? 0,
      sessions: Array.isArray(data.sessions)
        ? data.sessions.map((session) => ({
          createdAt: session.created_at,
          expiresAt: session.expires_at
        }))
        : []
    };
  }, []);

  // Listen for logout events from axios interceptor
  useEffect(() => {
    window.addEventListener('auth:logout', handleLogout);

    // Listen for impersonation expired events
    // Use a thunk to avoid stale closure with exitImpersonation
    const handleImpersonationExpired = async () => {
      console.log('Impersonation expired - restoring admin session');
      try {
        // Refresh to get admin's own token back (cookie still belongs to admin)
        const response = await axios.post('/v1/account/refresh');
        const { serviceToken, user } = response.data;

        setSession(serviceToken);
        dispatch({
          type: EXIT_IMPERSONATION,
          payload: { user }
        });
      } catch (err) {
        console.error('Exit impersonation failed:', err);
        // If refresh fails, just logout
        handleLogout();
      }
    };
    window.addEventListener('auth:impersonation_expired', handleImpersonationExpired);

    const handleBillingRequired = (event) => {
      const billing = event?.detail?.billing;
      if (billing) {
        setBillingState(billing);
      }
    };
    window.addEventListener('billing:required', handleBillingRequired);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:impersonation_expired', handleImpersonationExpired);
      window.removeEventListener('billing:required', handleBillingRequired);
    };
  }, [handleLogout, setBillingState]);

  // Initialize auth state on mount - attempt silent refresh
  useEffect(() => {
    const init = async () => {
      try {
        // Attempt silent refresh using httpOnly cookie
        const response = await axios.post('/v1/account/refresh');
        const { serviceToken, user } = response.data;

        setSession(serviceToken);
        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user
          }
        });
      } catch (err) {
        // Only logout on auth errors (401/403)
        // On 5xx, we might just have a transient issue
        if (err.response?.status === 401 || err.response?.status === 403) {
          dispatch({ type: LOGOUT });
        } else {
          // Transient error - still dispatch LOGOUT to show logged out state
          // but the cookie is still valid, user can retry
          console.error('Refresh failed (possibly transient):', err);
          dispatch({ type: LOGOUT });
        }
      }
    };

    init();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/v1/account/login', { email, password });
    const data = response.data;

    if (data?.mfaRequired) {
      setPendingMfaChallenge({
        challengeId: data.challengeId,
        methods: data.methods || ['totp', 'backup_code'],
        email
      });
      return data;
    }

    const { serviceToken, user } = data;
    setSession(serviceToken);
    setPendingMfaChallenge(null);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
  };

  const register = async (email, password, firstName, lastName, inviteCode, captchaToken) => {
    const response = await axios.post('/v1/account/register', {
      email,
      password,
      firstName,
      lastName,
      inviteCode,
      captchaToken
    });

    // Our backend returns { serviceToken, user } on successful registration
    const data = response.data;

    // Check if we got a successful response with user data
    if (data.serviceToken && data.user) {
      // Registration successful - auto-login the user
      setSession(data.serviceToken);
      setPendingMfaChallenge(null);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: data.user
        }
      });
      return data;
    }

    return data;
  };

  const logout = async () => {
    try {
      // Tell backend to revoke the refresh token
      await axios.post('/v1/account/logout');
    } catch (err) {
      // Ignore errors - we still clear local state
      console.error('Logout request failed:', err);
    }
    handleLogout();
  };

  const logoutAll = async () => {
    try {
      // Revoke all sessions for this user
      await axios.post('/v1/account/logout-all');
    } catch (err) {
      console.error('Logout-all request failed:', err);
    }
    handleLogout();
  };

  /**
   * Admin-only: Impersonate a target user
   * @param {string} targetUserId - The user_id to impersonate
   */
  const impersonate = async (targetUserId) => {
    const response = await axios.post('/v1/account/impersonate', { target_user_id: targetUserId });
    const { serviceToken, user, impersonated_by } = response.data;

    // Use impersonation session - disables auto-refresh
    setImpersonationSession(serviceToken);

    dispatch({
      type: IMPERSONATE,
      payload: {
        user,
        impersonatedBy: impersonated_by
      }
    });
  };

  /**
   * Exit impersonation and restore admin session
   * Uses refresh cookie to get a fresh admin access token
   */
  const exitImpersonation = async () => {
    try {
      // Clear impersonation flag BEFORE refresh to prevent interceptor issues
      window.__isImpersonating = false;

      // Refresh to get admin's own token back (cookie still belongs to admin)
      const response = await axios.post('/v1/account/refresh');
      const { serviceToken, user } = response.data;

      setSession(serviceToken);
      dispatch({
        type: EXIT_IMPERSONATION,
        payload: { user }
      });
    } catch (err) {
      console.error('Exit impersonation failed:', err);
      // If refresh fails, just logout
      handleLogout();
    }
  };

  const verifyMfaLogin = async (code) => {
    if (!pendingMfaChallenge?.challengeId) {
      throw new Error('No MFA login challenge is in progress');
    }

    const response = await axios.post('/v1/account/login/verify-mfa', {
      challengeId: pendingMfaChallenge.challengeId,
      code
    });

    const { serviceToken, user } = response.data;
    setSession(serviceToken);
    setPendingMfaChallenge(null);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
    return response.data;
  };

  const beginPasswordReset = async (email, captchaToken) => {
    await axios.post('/v1/account/forgot-password', {
      email,
      captchaToken
    });
  };

  const completePasswordReset = async (token, newPassword) => {
    await axios.post('/v1/account/reset-password', {
      token,
      newPassword
    });
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post('/v1/account/change-password', {
      currentPassword,
      newPassword
    });
    handleLogout();
  };

  const beginMfaSetup = async () => {
    const response = await axios.post('/v1/account/mfa/setup');
    return response.data;
  };

  const confirmMfaSetup = async (code) => {
    const response = await axios.post('/v1/account/mfa/verify-setup', { code });
    dispatch({
      type: UPDATE_PROFILE,
      payload: {
        user: {
          ...userRef.current,
          mfa_enabled: true
        }
      }
    });
    return response.data;
  };

  const disableMfa = async (password, code) => {
    await axios.post('/v1/account/mfa/disable', { password, code });
    dispatch({
      type: UPDATE_PROFILE,
      payload: {
        user: {
          ...userRef.current,
          mfa_enabled: false
        }
      }
    });
  };

  const regenerateMfaBackupCodes = async (password, code) => {
    const response = await axios.post('/v1/account/mfa/regenerate-backup-codes', { password, code });
    return response.data;
  };

  const updateProfile = useCallback(async (profilePatch = {}) => {
    try {
      const response = await axios.patch('/v1/users/me', profilePatch);
      const profile = response.data || {};

      // Keep auth-friendly keys while merging richer profile fields from /users/me.
      const mergedUser = {
        ...state.user,
        ...profile,
        id: profile.user_id ?? state.user?.id,
        user_id: profile.user_id ?? state.user?.user_id ?? state.user?.id,
        name: profile.display_name ?? state.user?.name,
        display_name: profile.display_name ?? state.user?.display_name ?? state.user?.name
      };

      dispatch({
        type: UPDATE_PROFILE,
        payload: { user: mergedUser }
      });

      return mergedUser;
    } catch (err) {
      throw new Error(getErrorMessage(err) || 'Profile update failed');
    }
  }, [state.user]);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider value={{
      ...state,
      login,
      logout,
      logoutAll,
      register,
      beginPasswordReset,
      completePasswordReset,
      changePassword,
      beginMfaSetup,
      confirmMfaSetup,
      disableMfa,
      regenerateMfaBackupCodes,
      verifyMfaLogin,
      pendingMfaChallenge,
      updateProfile,
      getSessionSummary,
      refreshBillingStatus,
      impersonate,
      exitImpersonation
    }}>
      {children}
    </JWTContext.Provider>
  );
}

export default JWTContext;

JWTProvider.propTypes = { children: PropTypes.node };
